import { useEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'
import clsx from 'clsx'
import { useCanEditModule } from '@/auth/useCanEditModule'
import type { Loan } from '@/types'
import type { ColumnId } from './types'
import { COLUMN_MAP } from './columns'
import { groupByStage, plainRows, type ScreenLayout } from './screenLayout'
import { PipelineTableCell } from './PipelineTableCell'

/** Horizontal gutter on header and row grids (`px-1` = 4px each side). */
const GRID_GUTTER_PX = 8

export function columnGridTrack(id: ColumnId): { track: string; minPx: number } {
  switch (id) {
    case 'property':
      return { track: 'minmax(170px, 1.5fr)', minPx: 170 }
    case 'sponsor':
    case 'borrower':
      return { track: 'minmax(120px, 1fr)', minPx: 120 }
    case 'loanAmount':
    case 'fullyFunded':
    case 'initialAdvance':
    case 'futureFunding':
    case 'fundedAmount':
      return { track: '96px', minPx: 96 }
    case 'stage':
      return { track: '132px', minPx: 132 }
    case 'location':
      return { track: '62px', minPx: 62 }
    case 'ltv':
    case 'dscr':
    case 'daysOpen':
    case 'daysInStatus':
    case 'units':
    case 'sf':
    case 'ltc':
    case 'ltvStabilized':
    case 'termMonths':
    case 'extensions':
    case 'ioMonths':
    case 'amortTerm':
    case 'rateType':
      return { track: '72px', minPx: 72 }
    case 'city':
    case 'country':
    case 'purpose':
    case 'spread':
    case 'coupon':
    case 'amortizationType':
    case 'debtYieldStabilized':
    case 'unitsMeasured':
    case 'amortOrIo':
      return { track: '104px', minPx: 104 }
    case 'ltvLtc':
      // "67.7% / 72.1%" and a derived side's "calc" mark on one line: 13
      // monospace glyphs (~78px at 10px, ~94px at 12px) plus the 8px tag
      // (~21px). At 104px (88px inside the padding) the pair alone overran in
      // Comfortable density; at 120px the marked pair stays inside its own
      // cell at either density.
      return { track: '120px', minPx: 120 }
    case 'brokerage':
    case 'brokerContact':
    case 'secondaryUnderwriter':
    case 'killReason':
    case 'killedAtStage':
    case 'dealStatus':
    case 'currentStatusReason':
    case 'currentStatusComments':
    case 'previousLoanStatus':
    case 'previousNote':
    case 'previousManager':
      return { track: '132px', minPx: 132 }
    case 'received':
    case 'killedAt':
    case 'currentStatusDate':
    case 'quoteLetterDate':
    case 'depositWireDate':
    case 'closeByDate':
      return { track: '96px', minPx: 96 }
    case 'quoteAge':
    case 'daysToCloseBy':
      return { track: '88px', minPx: 88 }
    case 'originated':
    case 'lastModified':
      // A desk stamp, "2026-10-29 14:05": 16 monospace glyphs (~96px at 10px)
      // plus the cell's 16px padding. At 96px the time ran into the next column.
      return { track: '116px', minPx: 116 }
    case 'type':
      // The pill holds the client's longest property type on one line:
      // "Manufactured Housing" measures 112.5px at 9px Inter, plus the cell's
      // 16px padding. At 96px "Student Housing" (85.7px) wrapped out of its row.
      return { track: '132px', minPx: 132 }
    case 'id':
    case 'product':
    case 'assignee':
    case 'underwriter':
    case 'sla':
    case 'debtYield':
    case 'priority':
    case 'completeness':
    case 'exceptions':
    case 'broker':
    case 'originator':
    case 'analyst':
    case 'closer':
      return { track: '96px', minPx: 96 }
    default: {
      const _never: never = id
      return _never
    }
  }
}

/**
 * The columns that will actually render, in order.
 *
 * The header and the rows both skip an id with no definition, but the grid
 * template was built from the raw list — so one unknown id laid down a track
 * nothing filled and shifted every column after it left. Ids outlive their
 * columns (a saved view still names `borrower`, retired when Sponsor and
 * Borrower merged), so the two must be derived from the same filtered list.
 */
export function visibleColumns(columns: ColumnId[]): ColumnId[] {
  return columns.filter((id) => COLUMN_MAP.has(id))
}

export function columnGridLayout(columns: ColumnId[]): { template: string; minWidth: number } {
  const tracks = visibleColumns(columns).map(columnGridTrack)
  return {
    template: tracks.map((track) => track.track).join(' '),
    minWidth: tracks.reduce((sum, track) => sum + track.minPx, 0) + GRID_GUTTER_PX,
  }
}

/**
 * Register B1: "zero and eight are hard to tell apart at current density. To be
 * resolved with density modes rather than a single size increase."
 *
 * Compact is what the desk has today and stays the default — a wider row is not
 * an improvement for someone scanning fifty deals, so nobody's view changes
 * without their asking. Roomy trades rows-per-screen for legibility.
 */
export type GridDensity = 'compact' | 'comfortable'

export const DENSITY: Record<GridDensity, { row: number; cell: string; head: string }> = {
  compact: { row: 26, cell: 'text-[10px]', head: 'text-[9px]' },
  comfortable: { row: 34, cell: 'text-[12px]', head: 'text-[10px]' },
}

const CELL_CONTROL = 'select, input, textarea, button, a[href], [contenteditable="true"], [role="combobox"], [role="button"]'

/**
 * Whether a double-click landed on a control inside a cell (the Responsible
 * Party selector, a review marker) rather than on the row. Such a control keeps
 * its own behaviour: opening the deal file over it would take the edit away.
 * The row's own full-width action button is the row, not a control in a cell.
 */
export function isCellControl(target: EventTarget | null, row: Element): boolean {
  if (!(target instanceof Element)) return false
  const control = target.closest(CELL_CONTROL)
  return control != null && control !== row && row.contains(control) && !control.hasAttribute('data-row-action')
}

export function DealGrid({
  loans,
  columns,
  selectedId,
  onSelect,
  onOpen,
  onHover,
  onHoverEnd,
  sort,
  onSort,
  onReorder,
  density = 'compact',
  layout = null,
  editableResponsibleParty = false,
}: {
  loans: Loan[]
  columns: ColumnId[]
  selectedId: string | null
  onSelect: (loan: Loan) => void
  /** Double-click opens the full deal workspace. Single click still just
   *  selects, so scanning the grid never throws a modal in the way. */
  onOpen?: (loan: Loan) => void
  /** The pointer resting on a row: the host warms that deal's dossier so a
   *  double-click opens it populated. */
  onHover?: (loan: Loan) => void
  onHoverEnd?: () => void
  sort: { id: ColumnId; direction: 'asc' | 'desc' }
  onSort: (id: ColumnId) => void
  /** Absent, the columns are fixed: headers do not drag and Alt+arrows move nothing. */
  onReorder?: (columns: ColumnId[]) => void
  /** Defaults to compact so an untouched caller renders exactly as before. */
  density?: GridDensity
  /**
   * The Pipeline report's layout (see screenLayout.ts): Backshop's headers over the columns and
   * the deals under their stage. Absent, the grid is the plain grid it always was.
   */
  layout?: ScreenLayout | null
  /** Live books persist picks through the existing versioned loan endpoint. */
  editableResponsibleParty?: boolean
}) {
  const canEditPipeline = useCanEditModule('pipeline')
  const canAssignResponsibleParty = editableResponsibleParty && canEditPipeline
  const parentRef = useRef<HTMLDivElement>(null)
  const [dragId, setDragId] = useState<ColumnId | null>(null)
  const [dragOverId, setDragOverId] = useState<ColumnId | null>(null)
  // The drag source is tracked in a ref as well as state: state drives the
  // visual treatment, but the ref is what `drop` reads, so a reorder never
  // depends on a React state flush landing between dragstart and drop.
  const dragIdRef = useRef<ColumnId | null>(null)
  // Distinguishes a real drag from a click, so dropping never also triggers a sort.
  const draggedRef = useRef(false)

  const metrics = DENSITY[density] ?? DENSITY.compact

  // The rows: every deal, or the deals under their stage headers. Both the headers and the deals
  // are rows of the one virtualized list, so a header scrolls with the deals beneath it.
  const rows = useMemo(
    () => (layout ? groupByStage(loans, layout.stages) : plainRows(loans)),
    [loans, layout],
  )

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => metrics.row,
    overscan: 16,
  })

  // react-virtual caches the size it measured per row. Changing estimateSize
  // alone leaves those cached values in place, so the rows overlap at the old
  // pitch until something else invalidates them.
  useEffect(() => {
    virtualizer.measure()
  }, [density, virtualizer])

  const { template, minWidth } = columnGridLayout(columns)
  const shown = visibleColumns(columns)

  const dropOn = (targetId: ColumnId) => {
    const source = dragIdRef.current
    if (!source || source === targetId || !onReorder) return
    const next = columns.filter((columnId) => columnId !== source)
    const targetIndex = next.indexOf(targetId)
    if (targetIndex < 0) return
    next.splice(targetIndex, 0, source)
    onReorder(next)
  }

  const moveByKeyboard = (id: ColumnId, delta: number) => {
    if (!onReorder) return
    const index = columns.indexOf(id)
    const target = index + delta
    if (index < 0 || target < 0 || target >= columns.length) return
    const next = [...columns]
    next.splice(index, 1)
    next.splice(target, 0, id)
    onReorder(next)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-amber-300/10 bg-[#0c0c0c]">
      {/* Header lives inside the same overflow scroller as the rows. A sibling
          header stayed put while the body scrolled sideways, so labels no
          longer sat over the cells they named. */}
      <div ref={parentRef} data-testid="deal-grid-scroller" className="min-h-0 flex-1 overflow-auto">
        <div className="min-w-full" style={{ minWidth }}>
          <div
            data-testid="deal-grid-header"
            className={clsx(
              'sticky top-0 z-10 grid gap-0 border-b border-amber-300/10 bg-[#121212] px-1 uppercase tracking-[0.1em] text-stone-500',
              metrics.head,
            )}
            style={{ gridTemplateColumns: template }}
          >
            {shown.map((columnId) => {
              const column = COLUMN_MAP.get(columnId)
              if (!column) return null
              const active = sort.id === columnId
              // The report's own header where the grid shows the report; the column's label otherwise.
              const label = layout?.headers.get(columnId) ?? column.label
              return (
                <div
                  key={columnId}
                  draggable={Boolean(onReorder)}
                  onDragStart={(event) => {
                    if (!onReorder) {
                      event.preventDefault()
                      return
                    }
                    dragIdRef.current = columnId
                    setDragId(columnId)
                    draggedRef.current = true
                  }}
                  onDragEnd={() => {
                    dragIdRef.current = null
                    setDragId(null)
                    setDragOverId(null)
                    // Let the trailing click land before re-enabling sort.
                    window.setTimeout(() => {
                      draggedRef.current = false
                    }, 0)
                  }}
                  onDragOver={(event) => {
                    event.preventDefault()
                    setDragOverId(columnId)
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    dropOn(columnId)
                    dragIdRef.current = null
                    setDragId(null)
                    setDragOverId(null)
                  }}
                  className={clsx(
                    'group relative flex items-center',
                    dragId === columnId && 'opacity-40',
                    dragOverId === columnId && dragId !== columnId && 'bg-amber-300/[0.12]',
                  )}
                >
                  {/* Absolutely positioned, and sized to sit inside the button's
                      own px-3 gutter. In the flex flow it reserved 14px (w-2.5 +
                      gap-1) that the body cells have no counterpart for, so every
                      header label rendered 14px to the right of the data beneath
                      it — visible as the whole header row being out of true. */}
                  {onReorder ? (
                    <GripVertical
                      aria-hidden
                      className="pointer-events-none absolute left-0.5 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-stone-700 opacity-0 transition group-hover:opacity-100"
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      if (draggedRef.current) return
                      onSort(columnId)
                    }}
                    onKeyDown={(event) => {
                      if (!event.altKey) return
                      if (event.key === 'ArrowLeft') {
                        event.preventDefault()
                        moveByKeyboard(columnId, -1)
                      }
                      if (event.key === 'ArrowRight') {
                        event.preventDefault()
                        moveByKeyboard(columnId, 1)
                      }
                    }}
                    title={
                      onReorder
                        ? `${label} — click to sort, drag to reorder (Alt+←/→ with keyboard)`
                        : `${label} — click to sort`
                    }
                    className={clsx(
                      // Every column centred, header and cell alike.
                      'inline-flex w-full items-center justify-center gap-0.5 px-1 py-1 text-center font-medium transition hover:text-amber-200',
                      onReorder && 'cursor-grab active:cursor-grabbing',
                      metrics.head,
                      active ? 'text-amber-200' : 'text-stone-500',
                    )}
                  >
                    {/* The report's headers are whole words ("In Place Debt Yield"): they wrap
                        to a second line rather than being cut at the column's width. */}
                    <span className={layout ? 'whitespace-normal break-words leading-tight' : 'truncate'}>{label}</span>
                    {active ? (
                      sort.direction === 'asc' ? <ArrowUp className="h-3 w-3 shrink-0" /> : <ArrowDown className="h-3 w-3 shrink-0" />
                    ) : null}
                  </button>
                </div>
              )
            })}
          </div>

          {loans.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-stone-500">
              No deals match the current query.
            </div>
          ) : (
            <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index]
                if (!row) return null
                if (row.kind === 'group') {
                  // A stage header, as the report prints it: the stage, then how many deals sit under it.
                  return (
                    <div
                      key={`stage:${row.key}`}
                      role="rowheader"
                      data-testid="deal-grid-stage"
                      className={clsx(
                        'absolute left-0 flex w-full items-center gap-2 border-b border-amber-300/15 bg-amber-300/[0.06] px-3 font-semibold uppercase tracking-[0.12em] text-amber-200',
                        metrics.cell,
                      )}
                      style={{
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <span>{row.label}</span>
                      <span className="font-normal normal-case tracking-normal text-stone-500">
                        {row.count} {row.count === 1 ? 'deal' : 'deals'}
                      </span>
                    </div>
                  )
                }
                const loan = row.loan
                return (
                  <div
                    key={loan.id}
                    data-testid="deal-grid-row"
                    data-loan-id={loan.id}
                    onClick={() => onSelect(loan)}
                    onDoubleClick={(event) => {
                      if (isCellControl(event.target, event.currentTarget)) return
                      onOpen?.(loan)
                    }}
                    onMouseEnter={onHover ? () => onHover(loan) : undefined}
                    onMouseLeave={onHoverEnd}
                    onFocus={onHover ? () => onHover(loan) : undefined}
                    title={onOpen ? 'Double-click to open the full deal workspace' : undefined}
                    className={clsx(
                      // tabular-nums gives every digit the same advance width, so a column
                      // of figures aligns and 0 against 8 is compared in one place rather
                      // than at a different offset on every row.
                      'absolute left-0 grid w-full gap-0 border-b border-white/[0.03] px-1 text-left tabular-nums transition',
                      metrics.cell,
                      selectedId === loan.id ? 'bg-amber-300/[0.07]' : 'hover:bg-white/[0.02]',
                    )}
                    style={{
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      gridTemplateColumns: template,
                    }}
                  >
                    {/* A separate row action keeps the native selector outside
                        a button while preserving keyboard access to the deal. */}
                    <button
                      type="button"
                      data-row-action
                      aria-label={`Open deal ${loan.property || loan.id}`}
                      className="absolute inset-0 h-full w-full rounded-none outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-amber-200"
                    />
                    {shown.map((columnId) => {
                      const column = COLUMN_MAP.get(columnId)
                      if (!column) return null
                      return (
                        <div
                          key={columnId}
                          className="relative flex min-w-0 items-center justify-center px-2"
                        >
                          <PipelineTableCell column={column} loan={loan} editableResponsibleParty={canAssignResponsibleParty} />
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
